import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EntityList from '../components/EntityList/EntityList';
import ChipBuilder from '../components/ChipBuilder';
import AddButton from '../components/AddButton/AddButton';
import { ExportCSVButton } from '../components/ExportButtons';
import { listUsers, isAdmin as checkAdmin, exportUsersCSV } from '../api';
import { LIST_PAGE_SIZE } from '../constants';
import useEntityList from '../hooks/useEntityList';
import styles from './UsersPage.module.css';

const ROLE_BADGE_CLASS = {
  administrator: styles.roleBadgeAdmin,
  supervisor:    styles.roleBadgeSupervisor,
  staff:         styles.roleBadgeStaff,
};

const COLUMNS = [
  {
    key: 'username',
    label: 'Username',
  },
  {
    key: 'role',
    label: 'Role',
    render: (value) => {
      const key = (value || '').toLowerCase();
      const modifier = ROLE_BADGE_CLASS[key] || styles.roleBadgeStaff;
      return (
        <span className={`${styles.roleBadge} ${modifier}`}>
          {value}
        </span>
      );
    },
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (value) => {
      if (!value) return '—';
      const date = new Date(value);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
  },
];

const CHIP_FIELDS = [
  {
    key: 'role',
    label: 'Role',
    type: 'select',
    options: [
      { value: 'Administrator', label: 'Administrator' },
      { value: 'Supervisor',    label: 'Supervisor' },
      { value: 'Staff',         label: 'Staff' },
    ],
  },
  {
    key: 'is_archived',
    label: 'Archived',
    type: 'select',
    options: [
      { value: 'false', label: 'Active' },
      { value: 'true',  label: 'Archived' },
    ],
  },
];

export default function UsersPage({ user }) {
  const navigate = useNavigate();
  const isAdmin = checkAdmin(user);

  const [chips, setChips] = useState([]);
  const [exportError, setExportError] = useState('');

  // Derive archived state from chips (server-side filter)
  const isArchived = useMemo(
    () => chips.find((c) => c.field === 'is_archived')?.value === 'true',
    [chips]
  );

  const filters = useMemo(() => ({ isArchived }), [isArchived]);

  const fetchFn = useCallback((cursor, { isArchived: archived }) =>
    listUsers({ isArchived: archived, cursor, limit: LIST_PAGE_SIZE }),
  []);

  const {
    items: rawItems,
    total,
    loading,
    fetchError,
    selectedIds,
    setSelectedIds,
    page,
    totalPages,
    handleNext,
    handlePrev,
  } = useEntityList({
    fetchFn,
    filters,
    // no archiveFn / unarchiveFn — UsersPage doesn't archive
  });

  // Client-side role filter (is_archived is server-side)
  const filteredItems = useMemo(() => {
    let result = rawItems;
    chips.forEach((chip) => {
      if (chip.field === 'role') {
        result = result.filter(
          (item) => (item.role || '').toLowerCase() === chip.value.toLowerCase()
        );
      }
      // is_archived handled server-side — skip client-side
    });
    return result;
  }, [rawItems, chips]);

  const exportParams = {
    isArchived,
    role: chips.find((c) => c.field === 'role')?.value,
  };

  const addButton = (
    <div style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
      <ExportCSVButton
        exportFn={() => exportUsersCSV(exportParams)}
        onError={setExportError}
        variant="list"
      />
      {isAdmin && <AddButton label="Add user" onClick={() => navigate('/users/new')} />}
    </div>
  );

  const filterPanel = (
    <div>
      <ChipBuilder
        chips={chips}
        onChipsChange={setChips}
        fields={CHIP_FIELDS}
      />
      {exportError && <p className={styles.exportError}>{exportError}</p>}
    </div>
  );

  return (
    <EntityList
      title="Users"
      columns={COLUMNS}
      items={filteredItems}
      total={total}
      loading={loading}
      errorMessage={fetchError}
      onRowClick={(item) => navigate(`/users/${item.id}`)}
      isAdmin={isAdmin}
      selectedIds={selectedIds}
      onSelectionChange={setSelectedIds}
      filterPanel={filterPanel}
      page={page}
      totalPages={totalPages}
      onPrev={handlePrev}
      onNext={handleNext}
      addButton={addButton}
      showArchived={isArchived}
    />
  );
}
